import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Card, message, Divider, Table, Typography, Button, Space, Progress, Dropdown } from "antd";
import { DownloadOutlined, PieChartOutlined, PrinterOutlined } from "@ant-design/icons";
import { HeaderPage } from "../../../components/headerPage";
import { HeaderTitle } from "../../../components/headerTitle";
import { useNavigate, useParams } from "react-router-dom";
import { getCampaignByIdApi } from "../../../api/formsColaboradoresCampaigns";
import { getReportByScoreApi } from "../../../api/formsColaboradoresReleaseForms";
import { useReactToPrint } from "react-to-print";
import { DataLoading } from "../../../components/loading";
import { PrintContent } from "./components/print";

const { Text } = Typography;

const getPercent = (value, total) => {
  if (total === 0 || value === 0) {
    return 0;
  }
  return (Number(value) / Number(total)) * 100;
};

export const FormulariosColaboradoresCampanhasRelatorioPontuacao = () => {
  const navigate = useNavigate();
  const refPrintResumido = useRef();
  const refPrint = useRef();
  const { id_campanhas } = useParams();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [campanha, setCampanha] = useState({});

  const items = [
    {
      key: "1",
      label: "Resumido",
      icon: <DownloadOutlined />,
      onClick: () => handlePrintResumido(),
    },
    {
      key: "2",
      label: "Detalhado",
      icon: <DownloadOutlined />,
      onClick: () => handlePrint(),
    },
  ];

  useEffect(() => {
    if (id_campanhas) {
      getCampaignById(id_campanhas);
      getReportByScore(id_campanhas);
    }
  }, [id_campanhas]);

  const getCampaignById = async (id) => {
    try {
      const { statusCode, response } = await getCampaignByIdApi(id);
      if (statusCode === 200) {
        if (!response.data) {
          navigate("/formularios-colaboradores/campanhas");
        }
        setCampanha(response.data);
      } else {
        message.error(response.message);
        navigate("/formularios-colaboradores/campanhas");
      }
    } catch (error) {
      message.error("Ops, houve algum erro!");
      navigate("/formularios-colaboradores/campanhas");
    }
  };

  const getReportByScore = async (id) => {
    setLoading(true);
    try {
      const { statusCode, response } = await getReportByScoreApi(id);
      if (statusCode === 200) {
        setData(response.data);
      } else {
        message.error(response.message);
      }
    } catch (error) {
      message.error("Ops, houve algum erro!");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Descrição",
      dataIndex: "descricao",
    },
    {
      width: 100,
      title: "Pontuação",
      dataIndex: "total_respostas",
    },
    {
      width: 100,
      title: "Satisfação",
      dataIndex: "total_pontuacao",
    },
    {
      width: 120,
      title: "(%) Satisfação",
      dataIndex: "percentual",
      render: (_, e) => (e.percentual ? `${e.percentual} %` : "-"),
    },
  ];

  const rowClassName = (record) => {
    if (record.questoes) {
      return "parent-row";
    }
    if (record.alternativas) {
      return "";
    }
    return "green-line";
  };

  const renderFooter = () => {
    return (
      <React.Fragment>
        <Table.Summary.Row style={{ backgroundColor: "#f7f7f7" }}>
          <Table.Summary.Cell index={0} />
          <Table.Summary.Cell index={1} />
          <Table.Summary.Cell index={2}>
            <Text strong>{data?.total_respostas ?? 0}</Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={3}>
            <Text strong>{data?.total_pontuacao ?? 0}</Text>
          </Table.Summary.Cell>

          <Table.Summary.Cell index={4}>
            <Text strong>{data?.percentual ?? 0} %</Text>
          </Table.Summary.Cell>
        </Table.Summary.Row>

        <Table.Summary.Row>
          <Table.Summary.Cell index={0}></Table.Summary.Cell>
          <Table.Summary.Cell index={1}>
            <Text strong>Total Respondentes (Concluídos)</Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell colSpan={3} index={4}>
            <Text strong>{data?.campanha?.total_concluidos}</Text>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </React.Fragment>
    );
  };

  const handlePrint = useReactToPrint({
    content: () => refPrint.current,
  });

  const handlePrintResumido = useReactToPrint({
    content: () => refPrintResumido.current,
  });

  return (
    <Row align="middle" justify="center" className="content-row">
      <Col span={24} align="middle" justify="center">
        <Col xs={24} sm={24} md={24} lg={22} xl={22} xxl={18} align="left">
          <div className="content-item">
            <HeaderPage
              title="Formulários Colaboradores"
              onClickBack={() => navigate("/formularios-colaboradores/campanhas")}
            />
            <HeaderTitle
              title={["Campanhas", "Relatório Pontuação"]}
              subtitle={`Campanha - ${campanha?.descricao ?? ""}`}
              icon={<PieChartOutlined />}
              loading={loading}
            />
            <Divider />

            {loading && <DataLoading />}
            {!loading && (
              <Card bordered={false} bodyStyle={{ padding: "3%" }}>
                <Space direction="vertical">
                  <Text className="line-break" style={{ fontSize: 16 }}>
                    <strong>Campanha:</strong> {data?.campanha?.descricao}
                  </Text>
                  <Text className="line-break" style={{ fontSize: 16 }}>
                    <strong>Formulário:</strong> {data?.descricao}
                  </Text>

                  <Text className="line-break" style={{ fontSize: 16 }}>
                    <strong>Parâmetro Avaliado:</strong>{" "}
                    {data?.campanha?.tipo_parametro === 1
                      ? data?.campanha?.colaborador
                      : data?.campanha?.descricao_parametro}
                  </Text>

                  <Text className="line-break" style={{ fontSize: 14 }}>
                    {data?.observacoes ?? ""}
                  </Text>
                </Space>
                <Divider />

                <Table
                  className="table-print"
                  bordered
                  loading={loading}
                  scroll={{ x: 700 }}
                  columns={columns}
                  dataSource={data?.grupos ?? []}
                  size="small"
                  rowKey={(e) => `${e.id}_grupo`}
                  expandable={{
                    expandRowByClick: true,
                    defaultExpandAllRows: true,
                    expandedRowRender: (e) => (
                      <Table
                        bordered
                        loading={loading}
                        scroll={{ x: 700 }}
                        columns={columns}
                        dataSource={e?.questoes ?? []}
                        size="small"
                        showHeader={false}
                        rowKey={(e) => (e.alternativas ? `${e.id}_questao` : `${e.id}_alternativa`)}
                        expandable={{
                          expandRowByClick: true,
                          childrenColumnName: "alternativas",
                        }}
                        rowClassName={rowClassName}
                        pagination={false}
                      />
                    ),
                  }}
                  rowClassName={rowClassName}
                  summary={renderFooter}
                  pagination={false}
                />

                <br />
                <Progress percent={getPercent(data?.campanha?.total_concluidos, data?.campanha?.total_formularios)} />

                <div ref={refPrint}>
                  <PrintContent
                    colunas={columns}
                    dados={data?.grupos ?? []}
                    campanha={data?.campanha?.descricao}
                    formulario={data?.descricao}
                    parametro={
                      data?.campanha?.tipo_parametro === 1
                        ? data?.campanha?.colaborador
                        : data?.campanha?.descricao_parametro
                    }
                    observacoes={data?.observacoes}
                    totalConlcluidos={data?.campanha?.total_concluidos}
                    totalRespostas={data?.total_respostas}
                    totalPontuacao={data?.total_pontuacao}
                    percentual={data?.percentual}
                    tipoRelatorio={"detalhado"}
                  />
                </div>

                <div ref={refPrintResumido}>
                  <PrintContent
                    colunas={columns}
                    dados={data?.grupos ?? []}
                    campanha={data?.campanha?.descricao}
                    formulario={data?.descricao}
                    parametro={
                      data?.campanha?.tipo_parametro === 1
                        ? data?.campanha?.colaborador
                        : data?.campanha?.descricao_parametro
                    }
                    observacoes={data?.observacoes}
                    totalConlcluidos={data?.campanha?.total_concluidos}
                    totalRespostas={data?.total_respostas}
                    totalPontuacao={data?.total_pontuacao}
                    percentual={data?.percentual}
                    tipoRelatorio={"resumido"}
                  />
                </div>
                <br />
                <Dropdown menu={{ items }}>
                  <Button icon={<PrinterOutlined />}>Imprimir</Button>
                </Dropdown>
              </Card>
            )}
          </div>
        </Col>
      </Col>
    </Row>
  );
};
